import type { ComponentProps } from "react";
import { ToggleButton, type ToggleButtonProps } from "react-aria-components";
import { tv } from "tailwind-variants";
import { cn } from "cn";
import { Button, type ButtonProps } from "@/components/ui/button.tsx";

export type FilterChipShape = "rounded" | "pill";

export const CLEARABLE_TRIGGER_CLASSNAME = "!rounded-r-none";

export const filterChipStyles = tv({
    base: "flex h-9 items-center gap-2 border px-3 text-sm font-medium transition-colors outline-hidden focus-visible:outline-hidden focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-0",
    variants: {
        isActive: {
            true: "border-ring bg-primary/6 text-primary [--btn-icon:var(--color-primary)] hover:bg-primary/10",
            false: "border-border bg-bg text-fg hover:border-muted-fg/40 hover:bg-bg"
        },
        shape: {
            rounded: "rounded-lg",
            pill: "rounded-full"
        }
    },
    defaultVariants: { isActive: false, shape: "rounded" }
});

export interface FilterChipButtonProps extends Omit<ButtonProps, "className"> {
    isActive?: boolean;
    shape?: FilterChipShape;
    isClearable?: boolean;
    className?: string;
}

export function FilterChipButton({ isActive, shape, isClearable, className, ...props }: FilterChipButtonProps) {
    return <Button intent="plain" size="sm" {...props} className={cn(filterChipStyles({ isActive, shape }), "cursor-pointer sm:px-3", isClearable && CLEARABLE_TRIGGER_CLASSNAME, className)} />;
}

export interface FilterChipToggleProps extends Omit<ToggleButtonProps, "className"> {
    shape?: FilterChipShape;
    className?: string;
}

// intent-ui-deviation: raw react-aria ToggleButton — Intent UI's Toggle fills `bg-secondary` when selected, where a
export function FilterChipToggle({ shape, className, ...props }: FilterChipToggleProps) {
    return <ToggleButton {...props} className={({ isSelected, isDisabled }) => cn(filterChipStyles({ isActive: isSelected, shape }), "cursor-pointer", isDisabled && "opacity-50 forced-colors:text-[GrayText]", className)} />;
}

export interface FilterChipProps extends ComponentProps<"div"> {
    isActive?: boolean;
    shape?: FilterChipShape;
}

export function FilterChip({ isActive, shape, className, ...props }: FilterChipProps) {
    return <div {...props} className={cn(filterChipStyles({ isActive, shape }), className)} />;
}
